/**
 * Rezertifizierung — Kampagnen, Positionen und Entscheidungen.
 *
 * Gegenstück zu `werkzeuge/rezertifizierung.mjs` und `datenbank/015-…`. Bis
 * zum 13.08.2026 zeigte `/rezertifizierung` vier Kampagnen aus
 * `entwurf/beispieldaten.ts`; „118 bestätigt, 17 entzogen, 52 offen" standen
 * als Zahlen im Quelltext. Der Fortschrittsring zeichnete sie getreu, und
 * bewegen konnte ihn niemand.
 *
 * ## Warum das anders liegt als bei der Funktionstrennung
 *
 * Eine SoD-Regel wird bei jedem Abruf neu gerechnet — ihr Befund ist eine
 * Eigenschaft des Bestands. Eine Kampagne ist ein **Vorgang mit Zustand**:
 * ihre Zahlen entstehen aus Entscheidungen und dürfen sich nicht ändern, weil
 * sich der Bestand ändert. Deshalb friert der Start den Umfang ein, und
 * deshalb hat diese Datei einen Schreibweg, wo `api/sod.ts` fast nur liest.
 *
 * ## Wo es heute rechnet
 *
 * Ausschliesslich im **Entwicklungsserver**, wie bei der Funktionstrennung.
 * `apps/api` kennt weder Sammlung noch Routen; die Tabellen aus `015` sind
 * angelegt, aber nichts schreibt sie fort. Die Ansicht erfindet dafür keinen
 * Ersatz — sie zeigt den Fehler und nennt den Grund.
 */

import { senden, type Seite } from './klient.js';
import { useAbruf, type Zustand } from './haken.js';
import { tokenHolen } from '../anmeldung/msal.js';

/* ------------------------------------------------------------------ Formen */

/** `CK_Kampagne_Zustand`. */
export const KAMPAGNENZUSTAENDE = [
  'entwurf', 'geplant', 'laufend', 'inNachbearbeitung', 'abgeschlossen', 'abgebrochen',
] as const;

/** `CK_Kampagne_Fristablauf` — und ausdrücklich kein `bestaetigen`. */
export const FRISTVERHALTEN = ['entziehen', 'eskalieren'] as const;

/** `CK_Kampagne_Sicht`. */
export const SICHTEN = ['person', 'objekt'] as const;

/**
 * Die Vorschrift, aus der die Positionen entstehen.
 *
 * Als JSON und nicht als Spalten, weil der Zuschnitt je Kampagne verschieden
 * ist — dieselbe Begründung, die `015` für `Umfangsregel` gibt. Eine leere
 * Regel erfasst den ganzen Bestand.
 */
export interface Umfangsregel {
  rollenkritikalitaet?: string[];
  rollen?: string[];
  oeCodes?: string[];
  assets?: string[];
  berechtigungsarten?: string[];
}

/** Gezählt aus den Positionen, nicht gespeichert. */
export interface Fortschritt {
  gesamt: number;
  offen: number;
  bestaetigt: number;
  entzogen: number;
  delegiert: number;
  verfallen: number;
  /** Nur `bestaetigt` und `entzogen` — verfallen ist keine Entscheidung. */
  entschieden: number;
  anteil: number;
}

export interface Kampagne {
  schluessel: string;
  bezeichnung: string;
  sicht: string;
  umfangsregel: Umfangsregel;
  beginn: string;
  stichtag: string;
  erinnerungTage: number;
  eskalationTage: number;
  verhaltenBeiFristablauf: string;
  zustand: string;
  gestartetAm: string | null;
  abgeschlossenAm: string | null;
  erstelltVon: string | null;
  /* `ohnePrueferAnzahl` stand hier bis zum 13.08.2026 und ist entfallen.
     Die Zahl gilt fuer den **Moment des Starts** — wer sie spaeter neu
     rechnete, bekaeme eine andere, weil sich der Bestand bewegt hat, und
     schriebe sie an eine Kampagne, fuer die sie nie galt. Sie steht dort, wo
     sie stimmt: in der Vorschau und in der Meldung nach dem Start. */
  fortschritt: Fortschritt;
}

export interface Position {
  positionSk: number;
  kampagne: string;
  kampagneBezeichnung: string;
  sicht: string;
  inhaberKontoSk: number;
  inhaberName: string;
  personId: string;
  oeCode: string;
  rolleId: string;
  rolleName: string;
  berechtigungId: string;
  asset: string;
  berechtigungsart: string;
  kritikalitaet: string;
  prueferKontoSk: number;
  prueferName: string;
  delegiertAnKontoSk: number | null;
  delegiertAnName: string | null;
  zustand: string;
  entschiedenAm: string | null;
  entschiedenVonName: string | null;
  entscheidungsgrund: string | null;
  fristBis: string;
  /**
   * Wann die Entscheidung am Bestand wirksam wurde — heute immer `null`.
   * `015` hält die Spalte getrennt vom Entscheidungszeitpunkt, damit sichtbar
   * bleibt, dass ein Entzug noch nicht angewendet ist. Eine Rezertifizierung,
   * deren Entzüge nur auf dem Papier stehen, ist schlimmer als keine.
   */
  angewendetAm: string | null;
}

/** Was eine Umfangsregel erfassen **würde** — vor dem Anlegen gerechnet. */
export interface Umfangsvorschau {
  positionen: number;
  personen: number;
  pruefer: number;
  rollen: number;
  ohnePruefer: number;
  ohnePrueferBeispiele: Array<{ inhaberName: string; rolleId: string; asset: string }>;
  beispiele: Array<{ inhaberName: string; prueferName: string; rolleId: string; asset: string }>;
}

/** Der Entwurf einer Kampagne, wie das Formular ihn schickt. */
export interface Kampagnenentwurf {
  schluessel: string;
  bezeichnung: string;
  sicht: string;
  umfangsregel: Umfangsregel;
  beginn: string;
  stichtag: string;
  erinnerungTage: number;
  eskalationTage: number;
  verhaltenBeiFristablauf: string;
}

/* ------------------------------------------------------------------- Lesen */

export const useKampagnen = (mandant: string, aus = false): Zustand<Seite<Kampagne>> =>
  useAbruf<Seite<Kampagne>>(aus ? null : '/api/rezertifizierung/kampagnen', mandant);

/**
 * Die Positionen einer Kampagne — serverseitig gefiltert und geblättert.
 *
 * Eine Kampagne über den ganzen Bestand hat vierstellig viele Positionen. Sie
 * vollständig zu holen und im Browser zu filtern wäre derselbe Fehler, den
 * `apps/api` an keiner Stelle macht.
 */
export const usePositionen = (
  mandant: string,
  frage: {
    kampagne?: string | null;
    zustand?: string | null;
    /** Nur, was beim angemeldeten Konto liegt — der Postkorb aus `015`. */
    meine?: boolean;
    seite?: number;
    groesse?: number;
    aus?: boolean;
  },
): Zustand<Seite<Position>> => {
  const p = new URLSearchParams();
  if (frage.kampagne) p.set('kampagne', frage.kampagne);
  if (frage.zustand) p.set('zustand', frage.zustand);
  if (frage.meine) p.set('meine', 'ja');
  p.set('seite', String(frage.seite ?? 1));
  p.set('groesse', String(frage.groesse ?? 50));
  return useAbruf<Seite<Position>>(
    frage.aus ? null : `/api/rezertifizierung/positionen?${p.toString()}`,
    mandant,
  );
};

/* --------------------------------------------------------------- Schreiben */

const mitToken = async (mandant: string, rumpf: unknown) =>
  ({ mandant, token: await tokenHolen(), rumpf });

/** Rechnet, was eine Umfangsregel erfassen würde — vor dem Anlegen. */
export const umfangVorschau = async (
  mandant: string,
  umfangsregel: Umfangsregel,
): Promise<Umfangsvorschau> =>
  senden<Umfangsvorschau>('/api/rezertifizierung/vorschau', 'POST',
    await mitToken(mandant, { umfangsregel }));

/** Legt eine Kampagne an — immer als Entwurf, nie als laufend. */
export const kampagneAnlegen = async (
  mandant: string,
  entwurf: Kampagnenentwurf,
): Promise<Kampagne> =>
  senden<Kampagne>('/api/rezertifizierung/kampagnen', 'POST',
    await mitToken(mandant, entwurf));

/**
 * Startet eine Kampagne: löst die Umfangsregel auf und friert das Ergebnis ein.
 *
 * Ein zweiter Start wird abgewiesen — er überschriebe den eingefrorenen Umfang
 * und damit den Nachweis darüber, was geprüft werden sollte.
 */
export interface Startergebnis {
  /** Wie viele Positionen eingefroren wurden. */
  positionen: number;
  /**
   * Wie viele der Umfang erfasst haette, fuer die es aber keinen Pruefer gibt,
   * der nicht der Inhaber ist. Sie sind **nicht** in der Kampagne.
   *
   * Die Zahl steht hier und nicht an der Kampagne: sie gilt fuer diesen
   * Augenblick. Spaeter neu gerechnet waere sie eine andere — der Bestand
   * bewegt sich — und stuende an einer Kampagne, fuer die sie nie galt.
   */
  ohnePruefer: number;
}

export const kampagneStarten = async (
  mandant: string,
  schluessel: string,
): Promise<Startergebnis> =>
  senden<Startergebnis>(
    `/api/rezertifizierung/kampagnen/${encodeURIComponent(schluessel)}/start`,
    'POST', await mitToken(mandant, {}),
  );

/**
 * Schliesst eine Kampagne ab.
 *
 * Der Server weist es ab, solange Positionen offen sind, und setzt sie
 * stattdessen auf `inNachbearbeitung`. Offene Positionen als bestätigt zu
 * zählen wäre die stille Zustimmung, die dieses Werkzeug verhindern soll.
 */
export const kampagneAbschliessen = async (
  mandant: string,
  schluessel: string,
): Promise<Kampagne> =>
  senden<Kampagne>(
    `/api/rezertifizierung/kampagnen/${encodeURIComponent(schluessel)}/abschliessen`,
    'POST', await mitToken(mandant, {}),
  );

/** Antwort einer Entscheidung: die Position und der Stand danach. */
export interface Entscheidungsergebnis extends Position {
  fortschritt: Fortschritt;
}

/**
 * Entscheidet eine Position.
 *
 * Der Server prüft die vier Augen und besteht beim Entzug auf einem Grund.
 * Beides steht zusätzlich als CHECK in `015` — die Ansicht nennt den Grund,
 * bevor die Datenbank ihn zurückwirft.
 */
export const positionEntscheiden = async (
  mandant: string,
  positionSk: number,
  zustand: 'bestaetigt' | 'entzogen',
  grund: string,
): Promise<Entscheidungsergebnis> =>
  senden<Entscheidungsergebnis>(
    `/api/rezertifizierung/positionen/${positionSk}/entscheidung`,
    'POST', await mitToken(mandant, { zustand, grund }),
  );

/** Reicht eine Position weiter. Nicht an den Inhaber — das wäre der Umweg. */
export const positionDelegieren = async (
  mandant: string,
  positionSk: number,
  anKontoSk: number,
): Promise<Entscheidungsergebnis> =>
  senden<Entscheidungsergebnis>(
    `/api/rezertifizierung/positionen/${positionSk}/delegation`,
    'POST', await mitToken(mandant, { anKontoSk }),
  );

/* ------------------------------------------------------------------ Helfer */

/** Farbtoken je Positionszustand — auch für die Ringe des Fortschritts. */
export function zustandsfarbe(zustand: string): string {
  if (zustand === 'bestaetigt') return '--success';
  if (zustand === 'entzogen') return '--danger';
  if (zustand === 'delegiert') return '--info';
  if (zustand === 'verfallen') return '--krit-sehrhoch';
  return '--warning';
}

/** Klasse des Markers je Kampagnenzustand. */
export function kampagnenklasse(zustand: string): string {
  if (zustand === 'abgeschlossen') return 'gut';
  if (zustand === 'laufend') return 'mittel';
  if (zustand === 'inNachbearbeitung') return 'warnung';
  if (zustand === 'abgebrochen') return 'fehler';
  return 'gering';
}

/** Tage bis zum Stichtag; negativ heisst überfällig. */
export function tageBis(stichtag: string, heute = new Date()): number {
  const ziel = new Date(stichtag + 'T00:00:00Z').getTime();
  const jetzt = Date.UTC(heute.getUTCFullYear(), heute.getUTCMonth(), heute.getUTCDate());
  return Math.round((ziel - jetzt) / 86400000);
}
